import React from 'react';
import { useState, useEffect } from "react";
import AxiosInstance from './../../services/AxiosInstance';
import { TICKET_ACTIVITY_URL } from '../../Constants/Constants';
import { USER_NAME } from '../../Constants/StorageConstants';
import {
    Box,
    Typography,
    Avatar,
    Chip,
    Divider,
    Skeleton,
    useTheme,
    Alert,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    Timeline,
    TimelineItem,
    TimelineSeparator,
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineOppositeContent,
} from '@mui/lab';
import {
    Edit as EditIcon,
    Assignment as AssignmentIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    Category as CategoryIcon,
    PriorityHigh as PriorityIcon,
    Speed as SpeedIcon,
    Warning as WarningIcon,
    Description as DescriptionIcon,
    Subject as SubjectIcon,
    Build as BuildIcon,
    Computer as ComputerIcon,
    CheckCircle as StatusIcon, InfoOutlined,
} from '@mui/icons-material';
import { format } from 'date-fns';
import InfoIcon from "@mui/icons-material/Info";

// Helper for avatar initials
function stringAvatar(name) {
    if (!name) return "";
    const parts = name.split(" ");
    return parts.length > 1
        ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
        : `${parts[0][0]}`.toUpperCase();
}

// Helper to get time ago
function getTimeAgo(dateString) {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return format(date, "MMM d, yyyy 'at' h:mm a");
}

// Helper to get activity icon
function getActivityIcon(activity) {
    // Check for status changes first as they're most important
    if (activity.status !== activity.newStatus) return <StatusIcon />;
    if (activity.priority !== activity.newPriority) return <PriorityIcon />;
    if (activity.urgency !== activity.newUrgency) return <SpeedIcon />;
    if (activity.impact !== activity.newImpact) return <WarningIcon />;
    if (activity.department !== activity.newDepartment) return <BusinessIcon />;
    if (activity.resolverGroup !== activity.newResolverGroup) return <AssignmentIcon />;
    if (activity.resolver !== activity.newResolver) return <PersonIcon />;
    if (activity.assetItem !== activity.newAssetItem) return <ComputerIcon />;
    if (activity.requestType !== activity.newRequestType) return <CategoryIcon />;
    if (activity.subject !== activity.newSubject) return <SubjectIcon />;
    if (activity.description !== activity.newDescription) return <DescriptionIcon />;
    return <EditIcon />;
}

// Helper to get activity color
function getActivityColor(activity) {
    if (activity.status !== activity.newStatus) return 'success';
    if (activity.priority !== activity.newPriority) return 'warning';
    if (activity.urgency !== activity.newUrgency) return 'error';
    if (activity.impact !== activity.newImpact) return 'error';
    if (activity.department !== activity.newDepartment) return 'info';
    if (activity.resolverGroup !== activity.newResolverGroup) return 'primary';
    if (activity.resolver !== activity.newResolver) return 'primary';
    if (activity.assetItem !== activity.newAssetItem) return 'info';
    if (activity.requestType !== activity.newRequestType) return 'secondary';
    if (activity.subject !== activity.newSubject) return 'secondary';
    if (activity.description !== activity.newDescription) return 'secondary';
    return 'primary';
}

// Helper to get icons for all changes in an activity
function getActivityIcons(activity) {
    const icons = [];

    if (activity.status !== activity.newStatus) icons.push({ icon: <StatusIcon />, type: 'status' });
    if (activity.priority !== activity.newPriority) icons.push({ icon: <PriorityIcon />, type: 'priority' });
    if (activity.urgency !== activity.newUrgency) icons.push({ icon: <SpeedIcon />, type: 'urgency' });
    if (activity.impact !== activity.newImpact) icons.push({ icon: <WarningIcon />, type: 'impact' });
    if (activity.department !== activity.newDepartment) icons.push({ icon: <BusinessIcon />, type: 'department' });
    if (activity.resolverGroup !== activity.newResolverGroup) icons.push({ icon: <AssignmentIcon />, type: 'resolverGroup' });
    if (activity.resolver !== activity.newResolver) icons.push({ icon: <PersonIcon />, type: 'resolver' });
    if (activity.assetItem !== activity.newAssetItem) icons.push({ icon: <ComputerIcon />, type: 'assetItem' });
    if (activity.requestType !== activity.newRequestType) icons.push({ icon: <CategoryIcon />, type: 'requestType' });
    if (activity.subject !== activity.newSubject) icons.push({ icon: <SubjectIcon />, type: 'subject' });
    if (activity.description !== activity.newDescription) icons.push({ icon: <DescriptionIcon />, type: 'description' });

    return icons.length > 0 ? icons : [{ icon: <EditIcon />, type: 'general' }];
}

// Helper to format change description
function formatChangeDescription(activity) {
    const changes = [];

    if (activity.department === null && activity.priority === null && activity.impact === null && activity.description === null && activity.subject === null && activity.requestType === null && activity.status === null) {
        changes.push({
            type: 'Ticket',
            text: `Ticket created with the details below.`,
            newValue: ''
        });
    }

    if (activity.department !== activity.newDepartment) {
        if (activity.department === null) {
            changes.push({
                type: 'department',
                text: `Department set to ${activity.newDepartment}`,
                newValue: activity.newDepartment
            });
        } else {
            changes.push({
                type: 'department',
                text: `Department changed from ${activity.department} to ${activity.newDepartment}`,
                oldValue: activity.department,
                newValue: activity.newDepartment
            });
        }
    }

    if (activity.priority !== activity.newPriority) {
        if (activity.priority === null) {
            changes.push({
                type: 'priority',
                text: `Priority set to ${activity.newPriority}`,
                newValue: activity.newPriority
            });
        } else {
            changes.push({
                type: 'priority',
                text: `Priority changed from ${activity.priority} to ${activity.newPriority}`,
                oldValue: activity.priority,
                newValue: activity.newPriority
            });
        }
    }

    if (activity.urgency !== activity.newUrgency) {
        if (activity.urgency === null) {
            changes.push({
                type: 'urgency',
                text: `Urgency set to ${activity.newUrgency}`,
                newValue: activity.newUrgency
            });
        } else {
            changes.push({
                type: 'urgency',
                text: `Urgency changed from ${activity.urgency} to ${activity.newUrgency}`,
                oldValue: activity.urgency,
                newValue: activity.newUrgency
            });
        }
    }

    if (activity.impact !== activity.newImpact) {
        if (activity.impact === null) {
            changes.push({
                type: 'impact',
                text: `Impact set to ${activity.newImpact}`,
                newValue: activity.newImpact
            });
        } else {
            changes.push({
                type: 'impact',
                text: `Impact changed from ${activity.impact} to ${activity.newImpact}`,
                oldValue: activity.impact,
                newValue: activity.newImpact
            });
        }
    }

    if (activity.description !== activity.newDescription) {
        if (activity.description === null) {
            changes.push({
                type: 'description',
                text: 'Description added',
                newValue: 'Added'
            });
        } else {
            changes.push({
                type: 'description',
                text: 'Description updated',
                newValue: 'Updated'
            });
        }
    }

    if (activity.subject !== activity.newSubject) {
        if (activity.subject === null) {
            changes.push({
                type: 'subject',
                text: `Subject set to ${activity.newSubject}`,
                newValue: activity.newSubject
            });
        } else {
            changes.push({
                type: 'subject',
                text: `Subject changed from ${activity.subject} to ${activity.newSubject}`,
                oldValue: activity.subject,
                newValue: activity.newSubject
            });
        }
    }

    if (activity.requestType !== activity.newRequestType) {
        if (activity.requestType === null) {
            changes.push({
                type: 'requestType',
                text: `Request Type set to ${activity.newRequestType}`,
                newValue: activity.newRequestType
            });
        } else {
            changes.push({
                type: 'requestType',
                text: `Request Type changed from ${activity.requestType} to ${activity.newRequestType}`,
                oldValue: activity.requestType,
                newValue: activity.newRequestType
            });
        }
    }

    if (activity.resolverGroup !== activity.newResolverGroup) {
        if (activity.resolverGroup === null) {
            changes.push({
                type: 'resolverGroup',
                text: `Resolver Group assigned to ${activity.newResolverGroup}`,
                newValue: activity.newResolverGroup
            });
        } else {
            changes.push({
                type: 'resolverGroup',
                text: `Resolver Group changed from ${activity.resolverGroup} to ${activity.newResolverGroup}`,
                oldValue: activity.resolverGroup,
                newValue: activity.newResolverGroup
            });
        }
    }

    if (activity.resolver !== activity.newResolver) {
        if (activity.resolver === null) {
            changes.push({
                type: 'resolver',
                text: `Resolver assigned to ${activity.newResolver}`,
                newValue: activity.newResolver
            });
        } else {
            changes.push({
                type: 'resolver',
                text: `Resolver changed from ${activity.resolver} to ${activity.newResolver}`,
                oldValue: activity.resolver,
                newValue: activity.newResolver
            });
        }
    }

    if (activity.assetItem !== activity.newAssetItem) {
        if (activity.assetItem === null) {
            changes.push({
                type: 'assetItem',
                text: `Asset Item assigned to ${activity.newAssetItem}`,
                newValue: activity.newAssetItem
            });
        } else {
            changes.push({
                type: 'assetItem',
                text: `Asset Item changed from ${activity.assetItem} to ${activity.newAssetItem}`,
                oldValue: activity.assetItem,
                newValue: activity.newAssetItem
            });
        }
    }

    if (activity.status !== activity.newStatus) {
        if (activity.status === null) {
            changes.push({
                type: 'status',
                text: `Status set to ${activity.newStatus}`,
                newValue: activity.newStatus
            });
        } else {
            changes.push({
                type: 'status',
                text: `Status changed from ${activity.status} to ${activity.newStatus}`,
                oldValue: activity.status,
                newValue: activity.newStatus
            });
        }
    }

    return changes;
}

function Activity(props) {
    const theme = useTheme();
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchActivities = async () => {
            setLoading(true);
            try {
                const response = await AxiosInstance.get(TICKET_ACTIVITY_URL + props.ticketId);
                setActivities(response.data);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch activities:', err);
                setError('Failed to load activity history');
            } finally {
                setLoading(false);
            }
        };

        fetchActivities();
    }, [props.ticketId]);

    if (loading) {
        return (
            <Box sx={{
                width: '100%',
                bgcolor: theme.palette.background.default,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
            }}>
                {/* Header Skeleton */}
                <Box sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    bgcolor: theme.palette.background.paper,
                }}>
                    <Skeleton variant="text" width="40%" height={24} />
                </Box>

                {/* Timeline Skeleton */}
                <Box sx={{ p: 2 }}>
                    <Timeline position="left" sx={{ p: 0, m: 0 }}>
                        {[...Array(3)].map((_, i) => (
                            <TimelineItem key={i} sx={{ '&:before': { display: 'none' } }}>
                                <TimelineSeparator>
                                    <Skeleton variant="circular" width={40} height={40} />
                                    {i < 2 && (
                                        <TimelineConnector sx={{
                                            bgcolor: theme.palette.divider,
                                            width: 2,
                                            height: 40,
                                        }} />
                                    )}
                                </TimelineSeparator>

                                <TimelineContent sx={{ py: 1, px: 2 }}>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                        p: 1.5,
                                        borderRadius: 2,
                                        bgcolor: theme.palette.background.paper,
                                        border: `1px solid ${theme.palette.divider}`,
                                    }}>
                                        <Skeleton variant="circular" width={32} height={32} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <Skeleton variant="text" width="30%" height={16} />
                                                <Skeleton variant="text" width="20%" height={14} />
                                            </Box>
                                            <Skeleton variant="text" width="80%" height={16} />
                                            <Skeleton variant="text" width="60%" height={16} />
                                        </Box>
                                    </Box>
                                </TimelineContent>
                            </TimelineItem>
                        ))}
                    </Timeline>
                </Box>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            </Box>
        );
    }

    if (!activities || activities.length === 0) {
        return (
            <Box
                sx={{
                    width: "100%",
                    height: "calc(100vh - 150px)",
                    bgcolor: theme.palette.background.default,
                    p: 2,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <InfoIcon
                    color="action"
                    sx={{ fontSize: 48, mb: 2, opacity: 0.7 }}
                />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    No activity recorded yet
                </Typography>
                <Typography variant="body2" color="text.disabled">
                    All ticket updates and changes will appear here.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            width: "100%",
            height: "calc(100vh - 150px)",
            bgcolor: theme.palette.background.default,
            p: 2,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column"
        }}>
            {/* Timeline */}
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
                <Timeline position="left" sx={{ p: 0, m: 0 }}>
                    {activities.map((activity, index) => {
                        const changes = formatChangeDescription(activity);
                        const activityIcons = getActivityIcons(activity);
                        const isLast = index === activities.length - 1;

                        return (
                            <TimelineItem key={activity.id} sx={{ '&:before': { display: 'none' } }}>
                                <TimelineSeparator>
                                    <TimelineDot
                                        color={getActivityColor(activity)}
                                        sx={{
                                            bgcolor: theme.palette[getActivityColor(activity)].main,
                                            color: theme.palette[getActivityColor(activity)].contrastText,
                                            position: 'relative',
                                        }}
                                    >
                                        {activityIcons.length === 1 ? (
                                            activityIcons[0].icon
                                        ) : (
                                            <Box sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: 0.5
                                            }}>
                                                {activityIcons.slice(0, 2).map((iconData, iconIndex) => (
                                                    <Box key={iconIndex} sx={{ fontSize: '0.7rem' }}>
                                                        {iconData.icon}
                                                    </Box>
                                                ))}
                                                {activityIcons.length > 2 && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontSize: '0.6rem',
                                                            fontWeight: 'bold',
                                                            color: 'inherit'
                                                        }}
                                                    >
                                                        +{activityIcons.length - 2}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                    </TimelineDot>
                                    {!isLast && (
                                        <TimelineConnector sx={{
                                            bgcolor: theme.palette.divider,
                                            width: 2,
                                        }} />
                                    )}
                                </TimelineSeparator>

                                <TimelineContent sx={{ py: 1, px: 2 }}>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                        p: 1.5,
                                        borderRadius: 2,
                                        bgcolor: theme.palette.background.paper,
                                        border: `1px solid ${theme.palette.divider}`,
                                        '&:hover': {
                                            bgcolor: theme.palette.action.hover,
                                        },
                                        transition: 'all 0.2s ease',
                                    }}>
                                        <Avatar
                                            alt={activity.modifiedBy}
                                            src={`/static/images/avatar/${activity.id}.jpg`}
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                bgcolor: theme.palette.primary.main,
                                                fontWeight: 600,
                                                fontSize: 14,
                                                color: theme.palette.getContrastText(theme.palette.primary.main),
                                            }}
                                        >
                                            {stringAvatar(activity.modifiedBy)}
                                        </Avatar>

                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mb: 1,
                                                flexWrap: 'wrap'
                                            }}>
                                                <Typography
                                                    fontWeight={600}
                                                    variant="body2"
                                                    sx={{
                                                        color: theme.palette.text.primary,
                                                        fontSize: '0.875rem'
                                                    }}
                                                >
                                                    {activity.modifiedBy}
                                                </Typography>

                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: theme.palette.text.secondary,
                                                        fontSize: '0.75rem'
                                                    }}
                                                >
                                                    {getTimeAgo(activity.createdOn)}
                                                </Typography>

                                                {/* Show change type indicators */}
                                                {activityIcons.length > 0 && (
                                                    <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                                                        {activityIcons.slice(0, 3).map((iconData, iconIndex) => (
                                                            <Tooltip
                                                                key={iconIndex}
                                                                title={iconData.type.charAt(0).toUpperCase() + iconData.type.slice(1)}
                                                                arrow
                                                            >
                                                                <Box sx={{
                                                                    fontSize: '0.75rem',
                                                                    color: theme.palette.text.secondary,
                                                                    display: 'flex',
                                                                    alignItems: 'center'
                                                                }}>
                                                                    {iconData.icon}
                                                                </Box>
                                                            </Tooltip>
                                                        ))}
                                                        {activityIcons.length > 3 && (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    fontSize: '0.7rem',
                                                                    color: theme.palette.text.secondary,
                                                                    fontWeight: 'bold'
                                                                }}
                                                            >
                                                                +{activityIcons.length - 3}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>

                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                {changes.map((change, changeIndex) => (
                                                    <Box key={changeIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: theme.palette.text.primary,
                                                                fontSize: '0.875rem',
                                                                lineHeight: 1.4,
                                                            }}
                                                        >
                                                            {change.text}
                                                        </Typography>
                                                        {change.newValue && (
                                                            <Chip
                                                                label={change.newValue}
                                                                size="small"
                                                                color={getActivityColor(activity)}
                                                                variant="outlined"
                                                                sx={{
                                                                    fontSize: '0.7rem',
                                                                    height: 20,
                                                                    '& .MuiChip-label': {
                                                                        px: 1,
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    </Box>
                                </TimelineContent>
                            </TimelineItem>
                        );
                    })}
                </Timeline>
            </Box>
        </Box>
    );
}

export default Activity;